/**
 * @fileoverview Company list display container (Table/Cards)
 * @description Renders table or card view via parent state; includes toolbar, pagination, loading/no-results states
 * Fetches initial view/list data if user logged in
 * @active Client component [CONFIRMED]
 *
 * @see {@link src/components/companies/NewCompanies.tsx} - Parent component
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/index.tsx} - Table view
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/index.tsx} - Toolbar
 * @see {@link src/providers/supabaseAuthProvider.client.tsx} - Auth state check
 * @see {@link src/hooks/useCompaniesListRequest.ts} - Company data fetch hook
 * @see {@link src/hooks/useGetCurrentView.ts} - Current view state hook
 * @see {@link src/store/newCompaniesList/newCompaniesList.selectors.ts} - Loading state selector (Redux)
 * @see {@link src/types/company.ts} - `Company` type definition
 */

import { memo, useEffect } from "react";

import { Box, Flex, Grid, useDisclosure } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import FilterIconSvg from "@/assets/svg/filter-icon.svg";
import { NoResultsComponent } from "@/components/ui/NoResultsComponent";
import { WaveSpinnerBlock } from "@/components/ui/WaveSpinner";
import { useActions } from "@/hooks/useActions";
import useCompaniesListRequest from "@/hooks/useCompaniesListRequest";
import useGetCurrentView from "@/hooks/useGetCurrentView";
import useViewFilterFeatures from "@/hooks/useViewFilterFeatures";
import { useSupabaseAuth } from "@/providers/supabaseAuthProvider.client";
import { useLazyGetAllListQuery } from "@/services/newList.service";
import { useLazyGetAllViewsQuery } from "@/services/newView.service";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";
import newListSelectors from "@/store/newLists/newLists.slectors";
import type { Company } from "@/types/company";

import CompaniesCards from "./companiesCards";
import CompaniesTable from "./companiesTable";
import PaginationCompanies from "./pagination";
import ViewListTools from "./viewListTools";
import AddToListModal from "./viewListTools/list/addtoListModal/AddToModal";
import PreferenceButton from "./viewListTools/shared/PreferenceButton";
import ViewPreferenceItem from "./viewListTools/shared/ViewPreferenceItem";
import ViewFilterModal from "./viewListTools/view/ViewFilterModal";

/**
 * NewCompaniesList component props
 *
 * Props for NewCompaniesList
 */
interface NewCompaniesListProps {
  /** Raw company data array */
  companies: Company[];
  /** Normalized company data for display */
  normalizeData: Company[];
}

/**
 * Container for company list; handles loading, no results, table/card rendering
 *
 * @component NewCompaniesList
 * @param {NewCompaniesListProps} props - Contains company data arrays
 * @returns {JSX.Element} Rendered list container or loading/no-results state
 */
const NewCompaniesList = ({
  companies,
  normalizeData,
}: NewCompaniesListProps): JSX.Element => {
  const controlModal = useDisclosure();
  const addToListModal = useDisclosure();
  const { getCompanies } = useCompaniesListRequest();
  const {
    clearSelectedCompanies,
    setArrayFilters,
    setViewParams,
    setIsLoadingCompanies,
  } = useActions();
  const { currentParams, currentView } = useGetCurrentView();
  const currentList = useSelector(newListSelectors.getCurrentList());

  const isLoading = useSelector(
    newCompaniesListSelectors.getIsLoadingCompanies()
  );
  const isAllCompanies = currentList?.id === "all companies";
  const { user, isLoading: isAuthLoading } = useSupabaseAuth(); // Use the new hook
  const isLoggedIn = !!user && !isAuthLoading; // Derive isLoggedIn

  /**
   * Initialize loading state on component mount and reset when data is available
   *
   * This effect sets the loading state to true when the component mounts,
   * and resets it to false once the companies data is available.
   * The cleanup function ensures the loading state is reset when the component unmounts.
   *
   * @see {@link src/components/ui/WaveSpinner.tsx} - Loading spinner component
   * @see {@link src/store/newCompaniesList/newCompaniesList.slice.ts} - Loading state management
   */
  useEffect(() => {
    // Show loading state immediately
    if (!companies || companies.length === 0) {
      setIsLoadingCompanies(true);
    } else {
      // Reset loading state when companies data is available
      setIsLoadingCompanies(false);
    }

    // Return cleanup function
    return () => {
      // Ensure loading state is reset when component unmounts
      setIsLoadingCompanies(false);
    };
  }, [companies, setIsLoadingCompanies]);

  const [getAllViews] = useLazyGetAllViewsQuery();
  const [getAllList] = useLazyGetAllListQuery();

  const loadingBlockHeight = currentParams?.limit * 32 + 96;

  const { filterBtnTitle, onOpenFilterHandler, removeFilterHandler } =
    useViewFilterFeatures({
      getCompanies,
      currentParams,
      currentView,
      controlModal,
      clearSelectedCompanies,
      setArrayFilters,
      setViewParams,
    });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (isLoggedIn) {
        getAllViews({});
        getAllList({});
      }
    }, 10);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Show loading state if:
  // 1. isLoading is true OR
  // 2. companies is undefined/null (initial load)
  if (isLoading || !companies) {
    return (
      <Grid placeItems={"center"} w={"100%"} h={`${loadingBlockHeight}px`}>
        <WaveSpinnerBlock />
      </Grid>
    );
  }

  // Only show "No companies found" if:
  // 1. We're not loading AND
  // 2. We have companies data AND
  // 3. Companies array is empty AND
  // 4. Either it's not "All Companies" view OR there are filters applied
  const hasFilters = currentParams?.arrayFilters || currentParams?.text;
  const shouldShowNoResults =
    !isLoading &&
    companies &&
    companies.length === 0 &&
    (!isAllCompanies || hasFilters);

  if (shouldShowNoResults) {
    return <NoResultsComponent isAllCompanies={isAllCompanies} />;
  }

  return (
    <>
      <ViewFilterModal controlModal={controlModal} />
      <AddToListModal addToControl={addToListModal} />
      <Flex direction={"column"} mx={"auto"} bg={"white"}>
        <Box display={{ base: "none", smd: "block" }}>
          <ViewListTools />
        </Box>

        <>
          <Box display={{ base: "none", smd: "block" }}>
            <CompaniesTable companies={normalizeData} />
          </Box>
          <Flex display={{ base: "flex", smd: "none" }} direction={"column"}>
            <PreferenceButton
              color={"lightGreen"}
              isActive={controlModal.isOpen || currentParams?.arrayFilters}
              isClose={currentParams?.arrayFilters}
              closeHandler={removeFilterHandler}
              style={{ mx: "20px", h: "36px", justify: "space-between" }}
            >
              <ViewPreferenceItem
                title={filterBtnTitle}
                icon={FilterIconSvg}
                handler={onOpenFilterHandler}
              />
            </PreferenceButton>
            <PaginationCompanies />
          </Flex>
          <Flex
            display={{ base: "flex", smd: "none" }}
            direction={"column"}
            rowGap={"12px"}
          >
            <CompaniesCards companies={normalizeData} />
          </Flex>
          <Flex w={"100%"}>
            <PaginationCompanies />
          </Flex>
        </>
      </Flex>
    </>
  );
};

export default memo(NewCompaniesList);
